import { useState } from "react";
import { useAuth } from "@/features/auth";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { userService } from "../services/userService";
import intervalsIcuLogo from "@/intervalsicu-logo-192.png";

export function IntervalsICUCard() {
  const { user, updateUser } = useAuth();
  const isConnected = user?.has_intervalsicu ?? false;

  const [athleteId, setAthleteId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);

  const handleSave = async () => {
    if (!athleteId.trim() || !apiKey.trim()) {
      setError("Both fields are required");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await userService.setIntervalsICU({
        athlete_id: athleteId.trim(),
        api_key: apiKey.trim(),
      });
      updateUser({ has_intervalsicu: true });
      setAthleteId("");
      setApiKey("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save credentials");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await userService.deleteIntervalsICU();
      updateUser({ has_intervalsicu: false });
      setShowDisconnectDialog(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to disconnect");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="relative group">
        {/* Card glow on hover */}
        <div
          className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100
                     transition-opacity duration-500 blur-sm"
          style={{
            background: `linear-gradient(
              135deg,
              oklch(0.75 0.15 55 / 0.2) 0%,
              transparent 50%,
              oklch(0.75 0.15 55 / 0.1) 100%
            )`,
          }}
        />

        {/* Card */}
        <div
          className="relative bg-card/80 backdrop-blur-sm border border-border
                     rounded-2xl p-6 shadow-xl"
        >
          {/* Top accent line */}
          <div
            className="absolute top-0 left-6 right-6 h-px"
            style={{
              background: `linear-gradient(
                90deg,
                transparent 0%,
                var(--primary) 50%,
                transparent 100%
              )`,
              opacity: 0.4,
            }}
          />

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              {/* Intervals.icu logo */}
              <img
                src={intervalsIcuLogo}
                alt="Intervals.icu"
                className="flex-shrink-0 w-10 h-10 rounded-lg"
              />
              <div>
                <h3 className="text-base font-medium text-foreground">
                  Intervals.icu
                </h3>
                <p className="text-xs text-foreground-muted">
                  Training data sync
                </p>
              </div>
            </div>

            {/* Status badge */}
            <div
              className={`
                px-2.5 py-1 rounded-full text-xs font-medium
                ${
                  isConnected
                    ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                    : "bg-muted text-foreground-muted border border-border"
                }
              `}
            >
              {isConnected ? "Connected" : "Not connected"}
            </div>
          </div>

          {/* Content */}
          {isConnected ? (
            <div className="space-y-4">
              <p className="text-sm text-foreground-muted">
                Your Intervals.icu account is connected. Koach can access your
                training data to provide personalized coaching insights.
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDisconnectDialog(true)}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                Disconnect account
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Why Intervals.icu - explanatory note */}
              <div
                className="mb-1 pl-3.5 border-l-2 py-0.5"
                style={{ borderColor: "oklch(0.75 0.15 55 / 0.5)" }}
              >
                <p className="text-sm text-foreground-muted leading-relaxed">
                  During our alpha, we use{" "}
                  <span className="text-foreground font-medium">Intervals.icu</span>{" "}
                  as a free aggregator — connect your watches, trackers, and fitness
                  apps there, and Koach will access your training data to give
                  personalized advice.
                </p>
                <p className="text-xs text-foreground-subtle mt-1.5">
                  First, link your devices in Intervals.icu{" "}
                  <a
                    href="https://intervals.icu/settings/connections"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-foreground-muted hover:text-primary transition-colors"
                  >
                    (Settings → Connections)
                  </a>,
                  then connect it to Koach below.
                </p>
              </div>

              {/* Step-by-step instructions */}
              <p className="text-sm text-foreground-muted mb-3 font-medium">
                How to get your credentials:
              </p>

              <ol className="space-y-2 mb-4">
                {[
                  <>Go to{" "}
                    <a
                      href="https://intervals.icu/settings"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline underline-offset-2"
                    >
                      Settings
                    </a>
                  </>,
                  <>Scroll down to <span className="text-foreground font-medium">Developer Settings</span></>,
                  <>Copy your <span className="text-foreground font-medium">Athlete ID</span></>,
                  <>Click <span className="text-foreground font-medium">API Key</span> → <span className="text-foreground font-medium">Generate</span></>,
                  <>Copy the generated key — you're all set!</>,
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-foreground-muted">
                    <span
                      className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary
                                 text-xs font-medium flex items-center justify-center mt-0.5"
                    >
                      {i + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>

              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label
                    htmlFor="athlete-id"
                    className="text-xs font-medium text-foreground-muted"
                  >
                    Athlete ID
                  </label>
                  <Input
                    id="athlete-id"
                    type="text"
                    placeholder="i12345"
                    value={athleteId}
                    onChange={(e) => setAthleteId(e.target.value)}
                    className="bg-background-elevated"
                  />
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="api-key"
                    className="text-xs font-medium text-foreground-muted"
                  >
                    API Key
                  </label>
                  <div className="relative">
                    <Input
                      id="api-key"
                      type={showApiKey ? "text" : "password"}
                      placeholder="Your API key"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      className="bg-background-elevated pr-16"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-foreground-muted
                                 hover:text-foreground transition-colors"
                    >
                      {showApiKey ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
              </div>

              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}

              <Button
                onClick={handleSave}
                disabled={isLoading || !athleteId.trim() || !apiKey.trim()}
                className="w-full"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Connecting...
                  </span>
                ) : (
                  "Connect account"
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Disconnect confirmation dialog */}
      <Dialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
        <DialogContent showCloseButton={false} className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Disconnect Intervals.icu?</DialogTitle>
            <DialogDescription>
              This will remove your Intervals.icu credentials. Koach will no
              longer have access to your training data.
            </DialogDescription>
          </DialogHeader>
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => setShowDisconnectDialog(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisconnect}
              disabled={isLoading}
            >
              {isLoading ? "Disconnecting..." : "Disconnect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
